import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { useAppClient } from '../../data/ClientProvider';
import { Button, FormField, Input } from '../../ui';
import { AuthLayout } from './AuthLayout';
import { authErrorMessage } from './authMessages';
import { DEFAULT_BUTTON } from './LoginPage';

/**
 * `/forgot-password` (accounts F1, D-F1.5), and it is TWO SCREENS behind one route.
 *
 * The instance decides which: `GET /api/auth/recovery` answers `manual` or `mail`, and a
 * screen that offered an email form on a `manual` instance would take an address, answer
 * "a link is on its way" and send nothing. So the form only exists where a message can
 * actually be sent, and otherwise the screen says the one true thing - ask an
 * administrator, who creates the link in Settings, People.
 *
 * IT REUSES THE SIGN-IN CONSTRUCTION (`spec/screens/login.md`), and since F4 it reuses the
 * one E3 REBUILT: the wordmark, a heading, one kit Lg field with its label above it, the
 * kit's tinted content-area default button (`DEFAULT_BUTTON` from `LoginPage`, not the glass
 * capsule this screen shipped with - ledger E-W4-46), and a plain 13px link under it. The
 * error sits beside the field, never as a banner at the top (item 7).
 */
export function ForgotPasswordPage() {
  const client = useAppClient();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  /**
   * The mode is a fact about the INSTANCE and it does not change while somebody looks at
   * this screen, so it is read once and never refetched. `retry: false`: an instance that
   * cannot answer this cannot mail either, and the manual sentence is the safe half.
   */
  const recovery = useQuery({
    queryKey: ['recovery-mode'],
    queryFn: () => client.auth.getRecoveryMode(),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  /** THE ONE ACTION IS UNAVAILABLE UNTIL THERE IS SOMETHING TO SEND, as on sign-in (R8). */
  const ready = email.trim() !== '';

  const submit = (): void => {
    if (email.trim() === '' || pending) return;
    setPending(true);
    setError(undefined);
    client.auth.requestPasswordReset(email).then(
      () => {
        setPending(false);
        setSent(true);
      },
      (cause: unknown) => {
        setPending(false);
        setError(authErrorMessage(cause));
      },
    );
  };

  /**
   * THE MANUAL SENTENCE IS ALSO THE FALLBACK while the mode is loading and if it fails.
   * A screen that flashed an email form and then took it away would have asked for
   * something it could not use.
   */
  if (recovery.data !== 'mail') {
    return (
      <AuthLayout
        title="Forgot password?"
        /**
         * ONE SENTENCE, and it is the one that says what to DO. The screen used to carry a
         * second line above this saying the same thing in other words, which left a reader
         * looking for the difference between them (round 2, finding 9).
         */
        subtitle="Ask an administrator for a reset link. They create it under Members."
      >
        <p className="text-2xs text-ink-subtle">
          <Link to="/login" className="text-accent">
            Sign in
          </Link>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Forgot password?"
      /** What the field is for, in one sentence and without a "we" (copy deck R8). */
      subtitle="Enter the address you sign in with to get a reset link."
    >
      {sent ? (
        <p className="text-sm text-ink-muted">
          If an account exists for {email}, a link is on its way. It expires in 60 minutes.
        </p>
      ) : (
        <form
          className="space-y-5"
          /** The browser's own validation bubble is off here for `LoginForm`'s reason. */
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <FormField label="Email" error={error}>
            {(props) => (
              <Input
                {...props}
                type="email"
                inputMode="email"
                autoComplete="username"
                autoCapitalize="none"
                /* R10: the hint is an EXAMPLE and the field keeps its label above it. */
                placeholder="name@example.com"
                /** The first field takes focus: this screen has nothing to read past. */
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={pending}
                invalid={error !== undefined}
              />
            )}
          </FormField>

          <Button
            type="submit"
            variant="push"
            block
            style={DEFAULT_BUTTON}
            disabled={!ready}
            loading={pending}
          >
            Send link
          </Button>
        </form>
      )}

      <p className="text-2xs text-ink-subtle">
        <Link to="/login" className="text-accent">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
